import React, {useEffect, useMemo, useState} from "react";
import {Grid, GridColumn} from "@progress/kendo-react-grid";
import {Button, DropDownButton, DropDownButtonItem} from "@progress/kendo-react-buttons";
import "./IdentityPipeline.css";
import {getFTPSettings} from "../../../actions/OnBoardingActions";
import {formatSchedule, semcastAxios, showErrorToastMessage, showSuccessToastMessage} from "../../../Utils/Common";
import {GROUPS_ACCESSIBLE_PIXELS, IDENTITY_PIPELINE, IDENTITY_PIPELINE_EXECUTE} from "../../../api/constants";
import CreateIdentityPipeline from "./CreateIdentityPipeline";
import {UserContext} from "../../../contexts/UserContext";
import EditIdentityPipeline from "./EditIdentityPipeline";
import {Dialog, DialogActionsBar} from "@progress/kendo-react-dialogs";
import LoadOverlay from "../../Components/SharedComponents/LoadOverlay";
import {GridColumn as Column} from "@progress/kendo-react-grid/dist/npm/GridColumn";
import {InputClearValue, InputSeparator, TextBox} from "@progress/kendo-react-inputs";
import {SvgIcon} from "@progress/kendo-react-common";
import {xIcon} from "@progress/kendo-svg-icons";
import {DropdownFilterCell} from "../../Components/Permission/dropdownFilterCell";
import {process} from "@progress/kendo-data-query";
import {Tooltip} from "@progress/kendo-react-tooltip";


const IdentityPipeline = () => {

    const {user, setUser} = React.useContext(UserContext);
    const [loadingRows, setLoadingRows] = useState(false);
    const [groupsWithNewOption, setGroupsWithNewOption] = useState([]);
    const [overlayActive, setOverlayActive] = useState(false);

    // table rows
    const [data, setData] = useState([]);

    const [connections, setConnections] = useState([]);
    const [loadingConns, setLoadingConns] = useState(false);
    const [connError, setConnError] = useState(null);

    const [groupsArr, setGroupsArr] = useState([]);
    const [newGroupName, setNewGroupName] = useState("");

    const [editMode, setEditMode] = useState("");
    const [deletePipelineObj, setDeletePipelineObj] = useState([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editPipelineObj, setEditPipelineObj] = useState({});
    const [deleteOverlay, setDeleteOverlay] = useState(false);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [pipelineToExecute, setPipelineToExecute] = useState(null);
    const [executeConfirmOpen, setExecuteConfirmOpen] = useState(false);
    const [executeOverlayActive, setExecuteOverlayActive] = useState(false);
    const [filter, setFilter] = useState({logic: "and", filters: []});
    const [sort, setSort] = useState([]);
    const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
    const DEFAULT_PAGE_STATE = {skip: 0, take: PAGE_SIZE_OPTIONS[1]};
    const [pageState, setPageState] = useState(DEFAULT_PAGE_STATE);


    const getAvailableGroups = async () => {
        try {
            let response = await semcastAxios.get(GROUPS_ACCESSIBLE_PIXELS, {withCredentials: true})
                .then((res) => {
                    setGroupsArr(res.data.data);
                }).catch((error) => {
                    if (error.response) {
                        showErrorToastMessage("Response Error: " + error.response.data.data, 10);
                        console.log(error.response);
                    }
                    if (error.request) {
                        showErrorToastMessage("Request Error: " + error.request.data.data, 10);
                        console.log(error.request);
                    }
                });
        } catch (error) {
            console.log(error)
            showErrorToastMessage("Error accessing Group information. " + error);
        }

    }

    const refreshTable = async () => {
        await Promise.allSettled([getAvailableGroups(), loadIdentityPipelines(), reloadConnections()]);
    }

    const runRefresh = async (showOverlay = false) => {
        if (showOverlay) setOverlayActive(true);
        await refreshTable();
        if (showOverlay) setOverlayActive(false);
    };

    useEffect(() => {
        let isMounted = true;
        runRefresh(true);
        const intervalId = setInterval(() => {
            if (isMounted) runRefresh(false);
        }, 20000);
        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, []);
    // When editing, load the record and prefill once groups/connections are ready

    useEffect(() => {
        let newGroupsList = groupsArr.length > 0 ? [{
            group_name: "Create New Group",
            id: "new"
        }, ...groupsArr] : [...groupsArr];
        if (!newGroupName) {
            console.log("OriginalSetting", newGroupsList);
            if (JSON.stringify(groupsWithNewOption) !== JSON.stringify(newGroupsList)) {
                setGroupsWithNewOption(newGroupsList);
            }
        }
    }, [groupsArr, newGroupName]);

    //helper functions

// Format epoch ms (or null) -> "YYYY-MM-DD HH:mm"
    function msToLocal(ms) {
        if (!ms) return "";
        const d = new Date(Number(ms));
        if (isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    const safeText = (value) => {
        if (value === null || value === undefined) return "-";
        if (typeof value === "string" && value.trim() === "") return "-";
        return value;
    };

    const buildOptions = (rows = [], field) => {
        const values = Array.from(
            new Set(
                rows
                    .map((row) => row[field])
                    .filter((val) => val !== undefined && val !== null)
            )
        ).sort((a, b) => a.localeCompare(b));
        return values;
    };

// Map one API record (from res.data.data[]) into a grid row
    function mapApiRowToGrid(r, idx = 0) {
        const sched = r?.schedule_properties?.schedule || null;
        const scheduleType = (sched?.type || "").toUpperCase();

        const pipelineTypeLabel = (() => {
            const raw = (r?.pipeline_type || "").toUpperCase();
            switch (raw) {
                case "HOUSEHOLD_EXPORT":
                    return "Household Export";
                case "PERSON_EXPORT":
                    return "Person Export";
                case "BUSINESS_EXPORT":
                    return "Business Export";
                case "FILE_EXCHANGE":
                    return "File Exchange";
                case "FOLDER_EXCHANGE":
                    return "Folder Exchange";
                default:
                    return raw ? raw.replace(/_/g, " ") : "-";
            }
        })();

        const fileTypeLabel = (() => {
            const raw = (r?.file_type || "").toUpperCase();
            switch (raw) {
                case "ADDRESS":
                    return "Address";
                case "BLOCKGRAPH_ID":
                    return "Blockgraph ID";
                case "DEVICE_ID":
                    return "Device ID";
                case "EMAIL":
                    return "Email";
                case "IMPRESSIONS":
                    return "Impressions";
                case "MD5_EMAIL":
                    return "MD5 Email";
                case "NPI":
                    return "NPI";
                case "SHA1_EMAIL":
                    return "SHA-1 Email";
                case "SHA1_IP":
                    return "SHA-1 IP";
                case "SHA256_EMAIL":
                    return "SHA-256 Email";
                case "UNKNOWN":
                    return "Unknown";
                default:
                    return raw ? raw.replace(/_/g, " ") : "-";
            }
        })

        const scheduleLabel = (() => {
            if (!scheduleType) return "-";
            if (scheduleType === "UNSCHEDULED") return "Unscheduled";
            return safeText(formatSchedule(sched));
        })();

        return {
            id: r?.id ?? idx + 1,
            name: safeText(r?.pipeline_name ?? ""),
            groupName: safeText(r?.group_name ?? ""),
            inputConnName: safeText(r?.incoming_ftp_settings_name ?? ""),
            outputConnName: safeText(r?.exhaust_ftp_settings_name ?? ""),
            compressionType: safeText(r?.compression_type ?? ""),
            // fileType: safeText(r?.file_type ?? ""),
            fileType: fileTypeLabel(),
            pipelineType: pipelineTypeLabel,
            schedule: scheduleLabel,
            scheduleType,
            updatedAt: safeText(msToLocal(r?.updated_at)),
            createdAt: safeText(msToLocal(r?.created_at)),
            owner: safeText(`${r?.first_name ?? ""} ${r?.last_name ?? ""}`.trim()),
            company: safeText(r?.company ?? ""),
            status: safeText(r?.status ?? ""),
            statusMessage: r?.status_message?.privateMessage ?? null,
        };
    }

    async function loadIdentityPipelines() {
        setLoadingRows(true);
        try {
            const res = await semcastAxios.get(IDENTITY_PIPELINE, {withCredentials: true});
            console.log(res)
            const list = Array.isArray(res?.data?.data) ? res.data.data : [];
            const rows = list.map((r, i) => mapApiRowToGrid(r, i));
            setData(rows);
        } catch (e) {
            console.error("Failed to load identity pipelines:", e);
            showErrorToastMessage("Failed to load identity pipelines.", 8);
            setData([]);
        } finally {
            setLoadingRows(false);
        }
    }


    // Map backend -> UI shape (id, name, type in S3|SFTP|FTPS)
    function normalizeConnections(list = []) {
        const safeDelim = (raw) =>
            typeof raw.delimiter === "string" && raw.delimiter.length === 1 ? raw.delimiter : "/";

        return list
            .map((raw, i) => {
                const id =
                    raw.id ??
                    raw.connection_id ??
                    raw.connId ??
                    raw.uuid ??
                    `conn_${Date.now()}_${i}`;

                // 1) Normalize type from API shape
                const proto = (raw.transfer_protocol || raw.type || raw.protocol || "")
                    .toString()
                    .trim()
                    .toUpperCase();

                let type;
                if (proto === "S3") type = "S3";
                else if (proto === "SFTP") type = "SFTP";
                else if (proto === "FTPS" || proto === "FTPES" || proto === "FTP_TLS") type = "FTPS";
                else if (proto === "FTP") {
                    // refuse plain FTP; only accept if we can infer TLS
                    type = undefined;
                }

                // 2) Heuristics if type still unknown (be permissive but safe)
                if (!type) {
                    if ((raw.transfer_protocol || "").toString().toLowerCase() === "s3") {
                        type = "S3";
                    } else if (raw.host && (Number(raw.port) === 22)) {
                        type = "SFTP";
                    } else if (
                        raw.host &&
                        (Number(raw.port) === 21 || Number(raw.port) === 990 || raw.passive_transfer_mode != null)
                    ) {
                        type = "FTPS";
                    }
                }

                // If still unknown, drop it.
                if (!["S3", "SFTP", "FTPS"].includes(type)) return null;

                // 3) Build friendly name + detail shape
                let name;
                let details;

                if (type === "S3") {
                    const region = raw.host || ""; // API puts region in host for S3
                    const delim = safeDelim(raw);
                    const path = (raw.starting_path || "").replace(/^\/+/, "");
                    const [bucket, ...rest] = path.split(delim);
                    const prefix = rest.length ? rest.join(delim) : "";
                    name =
                        raw.connection_name ||
                        raw.name ||
                        `S3 - ${bucket || path || region || id}${prefix ? `/${prefix}` : ""}`;
                    details = {
                        bucket: bucket || path || "",
                        prefix,
                        region,
                        accessKeyId: raw.user_name || "",
                        // secret isn't returned (good!) — get it only when creating/updating
                    };
                } else if (type === "SFTP") {
                    const port = Number(raw.port) || 22;
                    name =
                        raw.connection_name ||
                        raw.name ||
                        `SFTP - ${raw.host || "host"}${raw.starting_path ? ` ${raw.starting_path}` : ""}`;
                    details = {
                        host: raw.host || "",
                        port,
                        username: raw.user_name || "",
                        directory: raw.starting_path || "",
                    };
                } else {
                    // FTPS
                    const port = Number(raw.port) || 21; // Explicit TLS default
                    name =
                        raw.connection_name ||
                        raw.name ||
                        `FTPS - ${raw.host || "host"}${raw.starting_path ? ` ${raw.starting_path}` : ""}`;
                    details = {
                        host: raw.host || "",
                        port,
                        username: raw.user_name || "",
                        directory: raw.starting_path || "",
                        mode: port === 990 ? "IMPLICIT" : "EXPLICIT",
                        passive: !!raw.passive_transfer_mode,
                    };
                }

                return {id: String(id), name, type, details, _raw: raw};
            })
            .filter(Boolean);
    }

    async function reloadConnections() {
        setLoadingConns(true);
        setConnError(null);
        try {
            const resp = await getFTPSettings();
            const list = Array.isArray(resp?.data) ? resp.data : [];
            const norm = normalizeConnections(list);
            setConnections(norm);
            return norm; // <-- important for matching right after reload
        } catch (e) {
            setConnError(e?.message || "Failed to load connections");
            setConnections([]);
            return [];
        } finally {
            setLoadingConns(false);
        }
    }

    const inputConnectionOptions = useMemo(() => buildOptions(data, "inputConnName"), [data]);
    const outputConnectionOptions = useMemo(() => buildOptions(data, "outputConnName"), [data]);
    const exportTypeOptions = useMemo(() => buildOptions(data, "pipelineType"), [data]);
    const fileTypeOptions = useMemo(() => buildOptions(data, "fileType"), [data]);
    const scheduleOptions = useMemo(() => buildOptions(data, "schedule"), [data]);
    const groupOptions = useMemo(() => buildOptions(data, "groupName"), [data]);
    const statusOptions = useMemo(() => buildOptions(data, "status"), [data]);
    const compressionOptions = useMemo(() => buildOptions(data, "compressionType"), [data]);

    const dropdownFilterFactory = (options) => (props) => (
        <DropdownFilterCell
            {...props}
            data={options}
            defaultItem="Select..."
            height={"10px"}
        />
    );

    const inputConnFilterCell = useMemo(
        () => dropdownFilterFactory(inputConnectionOptions),
        [inputConnectionOptions]
    );
    const outputConnFilterCell = useMemo(
        () => dropdownFilterFactory(outputConnectionOptions),
        [outputConnectionOptions]
    );
    const exportTypeFilterCell = useMemo(
        () => dropdownFilterFactory(exportTypeOptions),
        [exportTypeOptions]
    );
    const fileTypeFilterCell = useMemo(
        () => dropdownFilterFactory(fileTypeOptions),
        [fileTypeOptions]
    );
    const scheduleFilterCell = useMemo(
        () => dropdownFilterFactory(scheduleOptions),
        [scheduleOptions]
    );
    const groupFilterCell = useMemo(
        () => dropdownFilterFactory(groupOptions),
        [groupOptions]
    );
    const statusFilterCell = useMemo(
        () => dropdownFilterFactory(statusOptions),
        [statusOptions]
    );
    const compressionFilterCell = useMemo(
        () => dropdownFilterFactory(compressionOptions),
        [compressionOptions]
    );

    const processedGrid = useMemo(
        () => process(data, {filter, sort}),
        [data, filter, sort]
    );
    const gridData = processedGrid.data;
    const pagedData = useMemo(
        () => gridData.slice(pageState.skip, pageState.skip + pageState.take),
        [gridData, pageState]
    );
    const totalGridRows = gridData.length;

    useEffect(() => {
        const total = totalGridRows;
        if (total === 0 && pageState.skip !== 0) {
            setPageState((prev) => ({...prev, skip: 0}));
            return;
        }
        if (total > 0 && pageState.skip >= total) {
            const remainder = total % pageState.take || pageState.take;
            const lastPageSkip = Math.max(total - remainder, 0);
            if (lastPageSkip !== pageState.skip) {
                setPageState((prev) => ({...prev, skip: lastPageSkip}));
            }
        }
    }, [totalGridRows, pageState.skip, pageState.take]);


    // add-record dialog
    const [showCreateRecord, setShowCreateRecord] = useState(false);


    const statusCellRender = React.useCallback((td, cellProps) => {
        if (cellProps.field !== "status") return td;
        const value = cellProps.dataItem?.status ?? "";
        const normalized = value?.toString().trim().toUpperCase();
        const isActive = normalized === "ACTIVE";
        const statusMessage = cellProps.dataItem?.statusMessage;
        const hasStatusMessage = statusMessage !== null && statusMessage !== undefined;
        const tooltipText = hasStatusMessage ? String(statusMessage) : "";
        // const color = isActive ? "rgb(25, 135, 84)" : "rgb(255, 193, 7)";
        return React.cloneElement(
            td,
            {
                ...td.props,
                className: `${td.props.className || ""} ip-status-cell ${isActive ? "ip-status-active" : "ip-status-other"}`,
                style: {...(td.props.style || {}), fontWeight: 600}
            },
            (
                <span className="ip-status-cell-content">
                    <span>{value}</span>
                    {hasStatusMessage && (
                        <Tooltip
                            position="right"
                            anchorElement="target"
                            tooltipStyle={{
                                width: "15rem",
                                borderRadius: "0.25rem",
                            }}
                        >
                            <i
                                className="fa fa-exclamation-circle tooltip-status-icon"
                                aria-hidden="true"
                                title={tooltipText}
                            />
                        </Tooltip>
                    )}
                </span>
            )
        );
    }, []);

    const columns = useMemo(
        () => [
            {field: "id", title: "ID", width: "60px", filterable: false},
            {field: "name", title: "Pipeline", width: "", filter: "text"},
            {field: "inputConnName", title: "Input Connection", width: "", filterCell: inputConnFilterCell},
            {field: "outputConnName", title: "Output Connection", width: "", filterCell: outputConnFilterCell},
            {field: "pipelineType", title: "Export Type", width: "", filterCell: exportTypeFilterCell},
            {field: "fileType", title: "File Type", width: "", filterCell: fileTypeFilterCell},
            {field: "schedule", title: "Schedule", width: "", filterCell: scheduleFilterCell},
            // {field: "owner", title: "Owner", width: "180px"},
            {field: "groupName", title: "Group", width: "", filterCell: groupFilterCell},
            // {field: "company", title: "Company", width: "180px"},
            {field: "status", title: "Status", width: "", filterCell: statusFilterCell},
            {field: "createdAt", title: "Created At", width: ""},
            {field: "updatedAt", title: "Last Updated", width: ""},
            {field: "compressionType", title: "Compression Type", width: "", filterCell: compressionFilterCell},


        ],
        [
            inputConnFilterCell,
            outputConnFilterCell,
            exportTypeFilterCell,
            scheduleFilterCell,
            groupFilterCell,
            statusFilterCell,
            compressionFilterCell
        ]
    );


    // ---------- CSV ----------
    const toCSV = (rows, cols) => {
        const esc = (v) => {
            if (v === null || v === undefined) return "";
            const s = String(v);
            if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };
        const header = cols.map(c => esc(c.title ?? c.field)).join(",");
        const body = rows.map(r => cols.map(c => esc(r[c.field])).join(",")).join("\n");
        return `${header}\n${body}`;
    };

    const downloadCSV = () => {
        const csv = toCSV(data, columns);
        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const now = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
        a.download = `identity-pipeline-${now}.csv`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
    };

    // ---------- add record ----------
    const openCreateRecord = () => {
        setShowCreateRecord(true);
    };
    const closeCreateRecord = () => {
        setShowCreateRecord(false);
    }
    const closeEditRecord = () => {
        setEditDialogOpen(false);
    }

    const handleDropdownSelect = (e, dataItem) => {
        switch (e.item.text) {
            case "Delete":
                setDeletePipelineObj([dataItem]);
                setOpenConfirmDelete(true);
                break;
            case "Edit":
                setEditDialogOpen(true);
                setEditPipelineObj(dataItem);
                setEditMode("Edit");
                break;
            case "Execute":
                setPipelineToExecute(dataItem);
                setExecuteConfirmOpen(true);
                break;
            default:
                break;
        }
    }
    const deletePipeline = async (pixelID, pixelLabel) => {

        try {
            let response = await semcastAxios.delete(IDENTITY_PIPELINE + "/" + pixelID,
                {withCredentials: true})
                .then(async (res) => {
                    if (res.data.code === 200) {
                        setDeleteConfirm("");
                        showSuccessToastMessage(`Pipeline ${pixelLabel} deleted`, 5);
                    }
                    await refreshTable().then(() => {
                        setOpenConfirmDelete(false);
                    })

                }).catch((error) => {

                    if (error.response) {
                        showErrorToastMessage("Response Error: " + error.response.data.data, 10);
                        console.log(error.response);
                    }
                    if (error.request) {
                        showErrorToastMessage("Request Error: " + error.request.data.data, 10);
                        console.log(error.request);
                    }
                });
        } catch (error) {
            showErrorToastMessage(error.response.data.data, 10);
            console.log(error.response.data.data);

        }


    }
    const executePipeline = async () => {
        if (!pipelineToExecute?.id) return;
        try {
            setExecuteOverlayActive(true);
            await semcastAxios.put(
                `${IDENTITY_PIPELINE_EXECUTE}${pipelineToExecute.id}`,
                {},
                {withCredentials: true}
            );
            showSuccessToastMessage("Pipeline executed.", 6);
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Failed to execute pipeline.";
            showErrorToastMessage(msg, 8);
        } finally {
            setExecuteOverlayActive(false);
            setExecuteConfirmOpen(false);
            setPipelineToExecute(null);
        }
    };

    const MyEditOnlyCommandCell = (props) => {
        const caseOwner = props.dataItem.user_id !== user.id;
        const caseAdmin = groupsArr.length < 1;
        const scheduleType = (props.dataItem?.scheduleType || "").toUpperCase();
        const showExecute = scheduleType === "UNSCHEDULED";
        const isInactive = (props.dataItem?.status || "").toUpperCase() !== "ACTIVE";

        return (
            <td className="idx-tag-gear-dropdown-div" key={props.dataItem.id}>
                <DropDownButton
                    className="btn btn-primary shadow btn-xs sharp idx-gear-button"
                    id={"identityDropdownMenuButton" + props.dataItem.id}
                    textField="text"
                    icon="cog"
                    title="Identity Options"
                    disabled={isInactive}
                    onItemClick={(e) => handleDropdownSelect(e, props.dataItem)}
                >
                    <DropDownButtonItem text="Edit"/>
                    {showExecute && <DropDownButtonItem text="Execute"/>}
                    <DropDownButtonItem text="Delete"/>
                </DropDownButton>
            </td>
        );
    };

    const handleSortChange = React.useCallback((e) => {
        setSort(e.sort);
        setPageState((prev) => ({...prev, skip: 0}));
    }, []);

    const handleFilterChange = React.useCallback((e) => {
        setFilter(e.filter || {logic: "and", filters: []});
        setPageState((prev) => ({...prev, skip: 0}));
    }, []);

    const handlePageChange = React.useCallback((event) => {
        setPageState({
            skip: event.page.skip,
            take: event.page.take,
        });
    }, []);

    const handleDeleteConfirmClear = React.useCallback(() => {
        setDeleteConfirm("");
    }, []);

    const handleDeleteConfirmChange = React.useCallback(event => {
        setDeleteConfirm(event.target.value);
    }, []);
    return (
        <div className="ip-wrap">

            <div className="ip-wrap">
                <div className="ip-toolbar">
                    <div className="ip-toolbar-left">
                        <Button className={"button-standard button-submit"} onClick={openCreateRecord} icon="plus">
                            Create New
                        </Button>
                    </div>
                    <div className="ip-toolbar-right">
                        <Button onClick={downloadCSV} icon="download">
                            Download CSV
                        </Button>
                    </div>
                </div>

                <Grid
                    data={pagedData}
                    cellRender={statusCellRender}
                    sortable
                    sort={sort}
                    onSortChange={handleSortChange}
                    resizable
                    filterable
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    pageable={{
                        buttonCount: 5,
                        pageSizes: PAGE_SIZE_OPTIONS,
                        info: true,
                        responsive: true
                    }}
                    skip={pageState.skip}
                    take={pageState.take}
                    onPageChange={handlePageChange}
                    total={totalGridRows}
                >
                    {columns.map(col => (
                        <GridColumn
                            key={col.field}
                            field={col.field}
                            title={col.title}
                            width={col.width}
                            filter={col.filter}
                            filterCell={col.filterCell}
                            filterable={col.filterable}
                            headerClassName={"ip-header"}
                            className={"ip-column"}
                        />
                    ))}
                    <GridColumn cell={MyEditOnlyCommandCell} filterable={false} width={"62px"}/>
                </Grid>

                {/* ---- Create Record Dialog ---- */}
                {showCreateRecord && <CreateIdentityPipeline
                    open={showCreateRecord}
                    onClose={closeCreateRecord}
                    onCreated={refreshTable}   // refresh the grid after success

                />}
                {editDialogOpen && <EditIdentityPipeline
                    open={editDialogOpen}
                    onClose={closeEditRecord}
                    onCreated={refreshTable}   // refresh the grid after success
                    pipelineId={editPipelineObj?.id}
                />}
                {openConfirmDelete && (
                    <div>
                        <Dialog
                            className={"idx-pixel-confirm-delete dialog-standard"}
                            title={"Confirm Delete"}
                            closeIcon={false}
                            width={550}
                        >
                            <LoadOverlay active={deleteOverlay}>
                                <div className={"pixel-dialog-text-container"}>
                                    <div className={"pixel-dialog-text pixel-dialog-text-2 mb-3"}>
                                        {`Deleting Tag `}<strong>{`${deletePipelineObj[0].id}`}</strong>{` will `}<strong>{`permanently remove `}</strong>{`it from your account and it will no longer be available.`}
                                    </div>
                                    <div className={"mb-4"}>
                                        <Grid data={deletePipelineObj}>
                                            <Column field="id" title="ID"/>
                                            <Column field="label" title="Description"/>
                                        </Grid>
                                    </div>
                                    <strong>{`Type "delete" below to verify that you want to delete this Tag.`}</strong>
                                    <TextBox
                                        style={{height: "50px", fontSize: "40px", marginTop: "20px", width: "170px"}}
                                        value={deleteConfirm} onChange={handleDeleteConfirmChange} placeholder="delete"
                                        suffix={() => <React.Fragment>
                                            {deleteConfirm !== "" &&
                                                <InputClearValue onClick={handleDeleteConfirmClear}>
                                                    <SvgIcon icon={xIcon}/>
                                                </InputClearValue>}
                                            <InputSeparator/>
                                        </React.Fragment>}/>
                                </div>
                                <DialogActionsBar>
                                    <div className={"idx-dialog-submit-button-div"}>
                                        <Button
                                            className={"button-standard button-white"}
                                            type={'button'}
                                            disabled={false}
                                            onClick={() => {
                                                setOpenConfirmDelete(false);
                                                setDeleteConfirm("");
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            className={"button-standard button-red"}
                                            type={'button'}
                                            onClick={async () => {
                                                await deletePipeline(deletePipelineObj[0].id, deletePipelineObj[0].label)
                                            }}
                                            disabled={deleteConfirm !== "delete"}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </DialogActionsBar>
                            </LoadOverlay>
                        </Dialog>

                    </div>
                )}
                {executeConfirmOpen && pipelineToExecute && (
                    <Dialog
                        className="idx-pixel-confirm-delete dialog-standard"
                        title="Execute Pipeline"
                        closeIcon={false}
                        width={450}
                    >
                        <LoadOverlay active={executeOverlayActive}>
                            <div className="pixel-dialog-text-container">
                                <div className="pixel-dialog-text pixel-dialog-text-2 mb-3">
                                    {`Run pipeline `}
                                    <strong>{pipelineToExecute.name || `#${pipelineToExecute.id}`}</strong>
                                    {` as a one-time execution?`}
                                </div>
                                <div>
                                    <strong>Schedule:</strong> {pipelineToExecute.schedule || "UNSCHEDULED"}
                                </div>
                            </div>
                            <DialogActionsBar>
                                <div className="idx-dialog-submit-button-div">
                                    <Button
                                        className="button-standard button-white"
                                        type="button"
                                        onClick={() => {
                                            setExecuteConfirmOpen(false);
                                            setPipelineToExecute(null);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className="button-standard button-submit"
                                        type="button"
                                        onClick={executePipeline}
                                    >
                                        Execute
                                    </Button>
                                </div>
                            </DialogActionsBar>
                        </LoadOverlay>
                    </Dialog>
                )}
            </div>
        </div>
    );
};

export default IdentityPipeline;
